import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader, KPIGrid, DataTable, StandardModal, Alert, Btn, FormField,
  inputStyle, selectStyle,
  colors, spacing, typography, radius,
} from '../design-system';
import axiosClient from '../api/axiosClient';
import { generatePassword } from '../utils/passwordGenerator';

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
const API  = `${BASE}/clients`;

const INDUSTRIES = [
  'Manufacturing', 'Construction', 'Healthcare', 'Education', 'Finance',
  'Retail', 'Technology', 'Transport & Logistics', 'Food & Beverage',
  'Energy & Utilities', 'Professional Services', 'Other',
];

const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];

export default function ClientManagement() {
  const userRole = localStorage.getItem('userRole');
  const navigate = useNavigate();
  const [clients, setClients]         = useState([]);
  const [certBodies, setCertBodies]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showCreate, setShowCreate]   = useState(false);
  const [showCreds, setShowCreds]     = useState(null);
  const [showAssign, setShowAssign]   = useState(null); // client row
  const [assignCbId, setAssignCbId]   = useState('');
  const [showDelete, setShowDelete]   = useState(null); // client row
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [msgType, setMsgType]         = useState('success');

  const token   = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [form, setForm] = useState({
    company_name: '', industry: '', size: '', country: '', address: '',
    website: '', description: '',
    user_email: '', user_username: '', user_full_name: '',
    user_password: generatePassword(),
  });

  const flash = (m, t = 'success') => {
    setMsg(m); setMsgType(t);
    setTimeout(() => setMsg(''), 4000);
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/`, { headers });
      if (!res.ok) throw new Error('Failed');
      setClients(await res.json());
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCertBodies = useCallback(async () => {
    if (userRole !== 'super_admin') return;
    try {
      const res = await fetch(`${API}/cert-bodies-list`, { headers });
      if (res.ok) setCertBodies(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchClients(); fetchCertBodies(); }, [fetchClients, fetchCertBodies]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/`, {
        method: 'POST', headers,
        body: JSON.stringify({
          company_name:   form.company_name,
          industry:       form.industry   || null,
          size:           form.size       || null,
          country:        form.country    || null,
          address:        form.address    || null,
          website:        form.website    || null,
          description:    form.description || null,
          user_email:     form.user_email,
          user_username:  form.user_username,
          user_password:  form.user_password,
          user_full_name: form.user_full_name || null,
        }),
      });
      if (!res.ok) {
        let detail = 'Failed to create client';
        try { const d = await res.json(); detail = d.detail || detail; } catch {}
        throw new Error(detail);
      }
      const created = await res.json();
      setShowCreate(false);
      setShowCreds({
        company:  created.company_name,
        email:    created.user_email,
        username: created.user_username,
        password: form.user_password,
      });
      setForm({
        company_name: '', industry: '', size: '', country: '', address: '',
        website: '', description: '',
        user_email: '', user_username: '', user_full_name: '',
        user_password: generatePassword(),
      });
      fetchClients();
    } catch (err) {
      flash('Error: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/${showDelete.id}`, { method: 'DELETE', headers });
      if (!res.ok) {
        let detail = 'Failed to delete client';
        try { const d = await res.json(); detail = d.detail || detail; } catch {}
        throw new Error(detail);
      }
      setShowDelete(null);
      flash(`${showDelete.name} has been deleted`);
      fetchClients();
    } catch (err) {
      flash('Error: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignCertBody = async () => {
    if (!assignCbId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/${showAssign.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ cert_body_id: parseInt(assignCbId) }),
      });
      if (!res.ok) {
        let detail = 'Failed to assign cert body';
        try { const d = await res.json(); detail = d.detail || detail; } catch {}
        throw new Error(detail);
      }
      setShowAssign(null);
      setAssignCbId('');
      flash(`Cert body assigned to ${showAssign.name}`);
      fetchClients();
    } catch (err) {
      flash('Error: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'name',                label: 'Company',    render: v => <strong>{v}</strong> },
    { key: 'industry',            label: 'Industry',   render: v => v || '—' },
    { key: 'size',                label: 'Size',       render: v => v || '—' },
    { key: 'cert_body_name',      label: 'Cert Body',  render: v => v || <span style={{ color: colors.textMuted, fontStyle: 'italic' }}>Not assigned</span> },
    { key: 'user_count',          label: 'Users',      render: v => v ?? 0 },
    { key: 'implementation_status', label: 'Status',   render: v => v || '—' },
    { key: 'created_at',          label: 'Created',    render: v => v ? new Date(v).toLocaleDateString() : '—' },
    {
      key: 'id',
      label: 'Actions',
      render: (v, row) => (
        <div style={{ display: 'flex', gap: spacing.xs }}>
          <Btn size="sm" variant="secondary" onClick={() => navigate(`/clients/${row.id}`)}>
            View
          </Btn>
          {userRole === 'super_admin' && (
            <Btn size="sm" variant="secondary" onClick={() => { setShowAssign(row); setAssignCbId(row.cert_body_id ? String(row.cert_body_id) : ''); }}>
              Assign CB
            </Btn>
          )}
          <Btn size="sm" variant="danger" onClick={() => setShowDelete(row)}>
            Delete
          </Btn>
        </div>
      ),
    },
  ];

  const kpis = [
    { label: 'Total Clients', value: clients.length,  icon: '🏢', color: colors.primary },
    { label: 'Active Users',  value: clients.reduce((s, c) => s + (c.user_count || 0), 0), icon: '👥', color: colors.success },
    { label: 'Assigned to CB', value: clients.filter(c => c.cert_body_id).length, icon: '🔗', color: colors.info },
  ];

  return (
    <div>
      <PageHeader
        title="Client Management"
        subtitle="Create and manage client companies linked to your organisation."
        actions={<Btn onClick={() => setShowCreate(true)}>+ Add Client</Btn>}
      />

      <KPIGrid kpis={kpis} columns={3} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg('')} />}

      <DataTable
        columns={columns}
        rows={clients}
        loading={loading}
        emptyMessage="No clients found. Add your first client above."
        keyField="id"
      />

      {/* Delete Confirmation Modal */}
      {showDelete && (
        <StandardModal
          title="Delete Client"
          onClose={() => setShowDelete(null)}
          width={440}
          danger
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowDelete(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDelete} loading={saving}>Delete</Btn>
            </>
          }
        >
          <Alert type="warning" message={`This will permanently delete "${showDelete.name}" and all associated users. This cannot be undone.`} />
        </StandardModal>
      )}

      {/* Assign Cert Body Modal */}
      {showAssign && (
        <StandardModal
          title={`Assign Cert Body — ${showAssign.name}`}
          onClose={() => { setShowAssign(null); setAssignCbId(''); }}
          width={460}
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setShowAssign(null); setAssignCbId(''); }}>Cancel</Btn>
              <Btn onClick={handleAssignCertBody} loading={saving} disabled={!assignCbId}>Assign</Btn>
            </>
          }
        >
          {showAssign.cert_body_name && (
            <Alert type="info" message={`Currently assigned to: ${showAssign.cert_body_name}`} />
          )}
          <div style={{ marginTop: spacing.md }}>
            <FormField label="Certification Body" required>
              <select value={assignCbId} onChange={e => setAssignCbId(e.target.value)} style={selectStyle}>
                <option value="">Select cert body...</option>
                {certBodies.map(cb => (
                  <option key={cb.id} value={cb.id}>{cb.name}</option>
                ))}
              </select>
            </FormField>
          </div>
        </StandardModal>
      )}

      {/* Create Client Modal */}
      {showCreate && (
        <StandardModal
          title="Add New Client"
          onClose={() => setShowCreate(false)}
          width={600}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" form="create-client-form" loading={saving}>Create Client</Btn>
            </>
          }
        >
          <form id="create-client-form" onSubmit={handleCreate}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>

              <div style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Company Details
              </div>

              <FormField label="Company Name" required>
                <input required value={form.company_name}
                  onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="Acme Ltd" style={inputStyle} />
              </FormField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                <FormField label="Industry">
                  <select value={form.industry}
                    onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                    style={selectStyle}>
                    <option value="">Select industry...</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </FormField>
                <FormField label="Company Size">
                  <select value={form.size}
                    onChange={e => setForm(f => ({ ...f, size: e.target.value }))}
                    style={selectStyle}>
                    <option value="">Select size...</option>
                    {SIZES.map(s => <option key={s} value={s}>{s} employees</option>)}
                  </select>
                </FormField>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                <FormField label="Country">
                  <input value={form.country}
                    onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    placeholder="United Kingdom" style={inputStyle} />
                </FormField>
                <FormField label="Website">
                  <input value={form.website}
                    onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                    placeholder="https://example.com" style={inputStyle} />
                </FormField>
              </div>

              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: spacing.md, fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.semibold, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Admin User Account
              </div>

              <FormField label="Full Name">
                <input value={form.user_full_name}
                  onChange={e => setForm(f => ({ ...f, user_full_name: e.target.value }))}
                  placeholder="Jane Smith" style={inputStyle} />
              </FormField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
                <FormField label="Email" required>
                  <input required type="email" value={form.user_email}
                    onChange={e => setForm(f => ({ ...f, user_email: e.target.value }))}
                    placeholder="jane@company.com" style={inputStyle} />
                </FormField>
                <FormField label="Username" required>
                  <input required value={form.user_username}
                    onChange={e => setForm(f => ({ ...f, user_username: e.target.value }))}
                    placeholder="jane_smith" style={inputStyle} />
                </FormField>
              </div>

              <FormField label="Temporary Password" required helper="Auto-generated — you can edit it">
                <div style={{ display: 'flex', gap: spacing.sm }}>
                  <input required value={form.user_password}
                    onChange={e => setForm(f => ({ ...f, user_password: e.target.value }))}
                    style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} />
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, user_password: generatePassword() }))}
                    style={{
                      padding: `0 ${spacing.md}`, background: colors.background,
                      border: `1px solid ${colors.border}`, borderRadius: radius.md,
                      cursor: 'pointer', fontSize: typography.fontSizes.sm, whiteSpace: 'nowrap',
                    }}>
                    Regenerate
                  </button>
                </div>
              </FormField>

            </div>
          </form>
        </StandardModal>
      )}

      {/* Credentials Display Modal */}
      {showCreds && (
        <StandardModal
          title="Client Created Successfully"
          onClose={() => setShowCreds(null)}
          width={480}
          footer={
            <>
              <Btn variant="secondary" onClick={() => navigator.clipboard.writeText(
                `Company: ${showCreds.company}\nEmail: ${showCreds.email}\nUsername: ${showCreds.username}\nPassword: ${showCreds.password}`
              )}>Copy All</Btn>
              <Btn onClick={() => setShowCreds(null)}>Done</Btn>
            </>
          }
        >
          <Alert type="success" message={`${showCreds.company} has been created.`} />
          <div style={{ marginTop: spacing.md, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {[
              { label: 'Company',  value: showCreds.company },
              { label: 'Email',    value: showCreds.email },
              { label: 'Username', value: showCreds.username },
              { label: 'Password', value: showCreds.password },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${spacing.sm} ${spacing.md}`, background: colors.background, borderRadius: radius.md }}>
                <span style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{label}</span>
                <code style={{ fontSize: typography.fontSizes.sm, color: colors.textPrimary }}>{value}</code>
              </div>
            ))}
          </div>
          <p style={{ marginTop: spacing.md, fontSize: typography.fontSizes.xs, color: colors.warning }}>
            Save these credentials now — the password cannot be retrieved later.
          </p>
        </StandardModal>
      )}
    </div>
  );
}
